import React, { useRef, useEffect, useState, useCallback } from 'react'

import { useNavigate } from 'react-router-dom'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePlaylists } from '@/core/hooks/usePlaylist'
import { useTranslation } from '@/core/i18n/useTranslation'
import Icon from '@/shared/components/atoms/Icon'
import { NavButton } from '@/shared/components/atoms/NavButton'
import { ActionMenuItem } from '@/shared/components/molecules/ActionDropdownMenu'
import { cn } from '@/shared/utils/helpers'

import PlaylistCarousel, { PlaylistCarouselRef } from './PlaylistCarousel'
import PlaylistSkeleton from './PlaylistSkeleton'
import { FilteredSection } from '../hooks/useFilteredPlaylistSections'

interface FilteredPlaylistSectionProps {
	section: FilteredSection
	onFavoriteClick?: (playlist: Playlist, isFavoritesCarousel: boolean) => void
	loadingPlaylistId?: number | null
	favoritePlaylistIds?: Set<number>
	onLoadingStateChange?: (isLoading: boolean) => void
	getDropdownOptions?: (playlist: Playlist) => ActionMenuItem[]
}

const FilteredPlaylistSection: React.FC<FilteredPlaylistSectionProps> = ({
	section,
	onFavoriteClick,
	loadingPlaylistId,
	favoritePlaylistIds,
	onLoadingStateChange,
	getDropdownOptions,
}) => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const carouselRef = useRef<PlaylistCarouselRef>(null)
	const [currentPage, setCurrentPage] = useState(1)

	// Build query params based on filter type
	const queryParams = React.useMemo(() => {
		const params: Record<string, any> = {
			public: true,
			per_page: 5,
			page: currentPage,
		}

		switch (section.type) {
			case 'mood':
				params.moods = section.filterValue
				break
			case 'content_category':
				params.content_category_ids = section.filterValue
				break
			case 'music_genre':
				params.music_genre_ids = section.filterValue
				break
		}

		return params
	}, [section, currentPage])

	// Fetch playlists for this filter
	const { data: playlistsData, isLoading } = usePlaylists({
		config: useAccountConfig({
			params: queryParams,
		}),
	})

	const playlists = playlistsData?.data || []
	const pagination = playlistsData?.meta?.pagination

	// Notify parent about loading state changes
	useEffect(() => {
		if (onLoadingStateChange) {
			onLoadingStateChange(isLoading)
		}
	}, [isLoading, onLoadingStateChange])

	// Handle "View all" click - navigate to filtered page
	const handleViewAllClick = useCallback(() => {
		// Don't navigate if disabled (only 1 page)
		if (pagination && pagination.total_pages <= 1) {
			return
		}

		const params = new URLSearchParams()
		params.set('public', 'true')

		switch (section.type) {
			case 'mood':
				params.set('moods', String(section.filterValue))
				break
			case 'content_category':
				params.set('content_category_ids', String(section.filterValue))
				break
			case 'music_genre':
				params.set('music_genre_ids', String(section.filterValue))
				break
		}

		navigate(`/music/all?${params.toString()}`)
	}, [navigate, section, pagination])

	// Handle page change for infinite scroll effect
	const handlePageChange = useCallback(
		(direction: 'next' | 'prev') => {
			if (!pagination || pagination.total_pages <= 1) {
				return
			}

			setCurrentPage((prev) => {
				if (direction === 'next') {
					// If on last page, wrap to first page
					if (prev >= pagination.total_pages) {
						return 1
					}
					return prev + 1
				} else {
					// If on first page, wrap to last page
					if (prev <= 1) {
						return pagination.total_pages
					}
					return prev - 1
				}
			})
		},
		[pagination]
	)

	const handleScrollStateChange = useCallback(() => {
		// Scroll state tracking for future use if needed
	}, [])

	// Don't render if no playlists and not loading
	if (!isLoading && playlists.length === 0) {
		return null
	}

	const canNavigate = pagination && pagination.total_pages > 1
	const hasMultiplePages = pagination && pagination.total_pages > 1

	return (
		<div className="mb-8">
			<div className="flex items-center justify-between mb-6">
				<h2 className="flex items-baseline gap-4 m-0 text-foreground text-2xl font-semibold leading-[130%]">
					{section.title}
					<button
						onClick={handleViewAllClick}
						disabled={!hasMultiplePages}
						className={cn(
							'flex items-end bg-transparent border-none text-foreground text-xs font-medium cursor-pointer underline p-0 m-0 transition-opacity duration-200',
							!hasMultiplePages
								? 'opacity-30 cursor-not-allowed'
								: 'opacity-60 hover:opacity-100'
						)}
					>
						{t('entities.music.view_all')}
					</button>
				</h2>
				{/* Navigation buttons for pagination - always show but disable if only 1 page */}
				<div className="hidden md:flex items-center gap-2">
					<NavButton
						onClick={() => handlePageChange('prev')}
						disabled={!canNavigate}
					>
						<Icon
							name="chevron-left"
							width="16px"
							height="16px"
							color="white"
						/>
					</NavButton>
					<NavButton
						onClick={() => handlePageChange('next')}
						disabled={!canNavigate}
					>
						<Icon
							name="chevron-right"
							width="16px"
							height="16px"
							color="white"
						/>
					</NavButton>
				</div>
			</div>
			{isLoading ? (
				<PlaylistSkeleton count={5} />
			) : (
				<PlaylistCarousel
					ref={carouselRef}
					playlists={playlists}
					getDropdownOptions={getDropdownOptions}
					onFavoriteClick={
						onFavoriteClick
							? (playlist) => onFavoriteClick(playlist, false)
							: undefined
					}
					showLocationIcon={true}
					showFavoriteIcon={true}
					isLoading={isLoading}
					onScrollStateChange={handleScrollStateChange}
					loadingPlaylistId={loadingPlaylistId}
					isFavoritesCarousel={false}
					favoritePlaylistIds={favoritePlaylistIds}
				/>
			)}
		</div>
	)
}

export default FilteredPlaylistSection
